import React, { useState, useEffect, useMemo, useRef } from "react";
import Swal from "sweetalert2";
import { 
  FaSearch, FaChevronDown, FaChevronUp, FaTrashAlt, FaMoneyBillWave, 
  FaPlus, FaTimes, FaEdit, FaPrint, FaLock, FaFileExport 
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Helmet } from "react-helmet-async";
import axiosInstance from "../../api/axiosInstance";
import Pagination from "../Pagination/Pagination";
import "./AddNidhiDonation.css";

const DonorDropdown = ({ value, placeholder, donors, searchKey, onSelect, onClear, disabled }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const ref = useRef(null);
  const listRef = useRef(null);

  const filtered = donors.filter((d) => (d[searchKey] || "").toLowerCase().includes(search.toLowerCase()));

  useEffect(() => { setHighlighted(0); }, [search, open]);
  
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && listRef.current) {
      const el = listRef.current.querySelector('.protsahan-nidhi-donation-list-in-admin-add-nidhi-drop-highlighted');
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlighted, open]);

  const handleKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlighted((h) => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlighted((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered.length > 0 && highlighted < filtered.length) { onSelect(filtered[highlighted]); setOpen(false); setSearch(""); }
    }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-filter-wrap" ref={ref} style={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
      <div className={`protsahan-nidhi-donation-list-in-admin-add-nidhi-filter-trigger ${open ? "active" : ""}`} onClick={() => { if(!disabled) { setOpen((o) => !o); setSearch(""); } }}>
        <span className="protsahan-nidhi-donation-list-in-admin-add-nidhi-filter-text" style={{ color: value ? "#2c3e50" : "#334155", fontWeight: value ? "600" : "400" }}>{value || placeholder}</span>
        <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-trigger-icons">
          {value && !disabled && <FaTimes className="protsahan-nidhi-donation-list-in-admin-add-nidhi-clear-icon" onClick={(e) => { e.stopPropagation(); onClear(); }} title="Clear Selection" />}
          <FaChevronDown size={10} style={{ color: "#64748b" }} />
        </div>
      </div>
      {open && (
        <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-filter-menu" onClick={(e) => e.stopPropagation()}>
          <input type="text" placeholder={`Search ${placeholder.toLowerCase()}...`} value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={handleKey} autoFocus />
          <ul ref={listRef}>
            {filtered.length > 0 ? filtered.map((d, i) => (
              <li key={d._id} className={i === highlighted ? "protsahan-nidhi-donation-list-in-admin-add-nidhi-drop-highlighted" : ""} onMouseEnter={() => setHighlighted(i)} onClick={() => { onSelect(d); setOpen(false); setSearch(""); }}>
                {d[searchKey]}
              </li>
            )) : <li className="protsahan-nidhi-donation-list-in-admin-add-nidhi-drop-empty">No results found</li>}
          </ul>
        </div>
      )}
    </div>
  );
};

const EF_TYPE = "Encouragement Fund (प्रोत्साहन निधि)";
const MODES = ["Cash", "UPI", "Bank Transfer", "Cheque", "NEFT", "RTGS", "Online Gateway"];

const AddNidhiDonation = () => {
  const navigate = useNavigate();
  const [donors, setDonors] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");
  const [modeFilter, setModeFilter] = useState("");
  const [dateFilter, setDateFilter] = useState(""); 

  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedDonor, setSelectedDonor] = useState(null);
  const [editDonationData, setEditDonationData] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedDonationId, setExpandedDonationId] = useState(null); 
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const [agreed, setAgreed] = useState(false); 

  const [form, setForm] = useState({
    donationType: EF_TYPE, amount: "5000", date: new Date().toISOString().split("T")[0], mode: "Cash", refNo: "", receiptNo: ""
  });

  useEffect(() => { fetchDonors(); }, []);

  const fetchDonors = async () => {
    try {
      const res = await axiosInstance.get("/api/donors");
      setDonors(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch (err) { console.error(err); }
  };

  const activeDonors = useMemo(() => donors.filter((d) => !d.isDeleted), [donors]);

  const getMissingFieldsForNidhi = (donor) => {
    if (!donor) return [];
    const missing = [];
    if (!donor.name?.toString().trim()) missing.push("Full Name");
    if (!donor.fatherName?.toString().trim()) missing.push("Father's Name");
    if (!donor.gender?.toString().trim()) missing.push("Gender");
    
    const cleanAadhar = (donor.aadharNo || "").toString().replace(/\D/g, "");
    if (!cleanAadhar) {
      missing.push("Aadhaar Card Number");
    } else if (cleanAadhar.length !== 12) {
      missing.push("Valid 12-digit Aadhaar Card");
    }

    const cleanPan = (donor.panNo || "").toString().trim();
    if (cleanPan && cleanPan.length !== 10) {
      missing.push("Valid 10-character PAN Card");
    }

    if (!donor.state?.toString().trim()) missing.push("State");
    if (!donor.district?.toString().trim()) missing.push("District");
    if (!donor.currentAddress?.toString().trim()) missing.push("Current Address");
    if (!donor.permanentAddress?.toString().trim()) missing.push("Permanent Address");
    return missing;
  };

  const missingProfileFields = useMemo(() => {
    return getMissingFieldsForNidhi(selectedDonor);
  }, [selectedDonor]);

  const isFirstTimeDonor = useMemo(() => {
    if (!selectedDonor) return true;
    const sortedNidhis = selectedDonor.donations?.filter(
      d => d.donationType === EF_TYPE && d.txnStatus === "Success"
    ).sort((a,b) => new Date(a.date) - new Date(b.date)) || [];
    
    if (editDonationData) {
      return sortedNidhis.length > 0 && sortedNidhis[0]._id === editDonationData.donationId;
    }
    return sortedNidhis.length === 0;
  }, [selectedDonor, editDonationData]);

  const groupedDonors = useMemo(() => {
    let list = [];

    donors.forEach((d) => {
      if (d.isDeleted) return;

      const userNidhiDonations = (d.donations || [])
        .filter((don) => don.donationType === EF_TYPE && (don.txnStatus === "Success" || !don.txnStatus))
        .map((don) => ({
          ...don,
          donorId: d._id,
          memberId: d.memberId,
          name: d.name,
          fatherName: d.fatherName,
          occupation: d.occupation,
          mobileNo: d.mobileNo,
          email: d.email,
          aadharNo: d.aadharNo,
          panNo: d.panNo,
          permanentAddress: d.permanentAddress,
          currentAddress: d.currentAddress,
          state: d.state,
          district: d.district,
          isActive: don.isActive !== false
        }));

      if (userNidhiDonations.length > 0) {
        const sortedDonations = userNidhiDonations.sort((a, b) => new Date(b.date) - new Date(a.date));
        
        list.push({
          ...d,
          nidhiDonationsList: sortedDonations,
          latestDonationDate: new Date(sortedDonations[0].date)
        });
      }
    });

    if (searchFilter) {
      const q = searchFilter.toLowerCase();
      list = list.filter((l) => 
        (l.memberId || "").toLowerCase().includes(q) || 
        (l.name || "").toLowerCase().includes(q) || 
        (l.mobileNo || "").includes(q)
      );
    }

    if (modeFilter) {
      list = list.filter((l) => l.nidhiDonationsList.some((don) => don.mode === modeFilter));
    }

    if (dateFilter) {
      list = list.filter((l) => 
        l.nidhiDonationsList.some((don) => {
          const d = new Date(don.date);
          const localDateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          return localDateStr === dateFilter;
        })
      );
    }

    return list.sort((a, b) => b.latestDonationDate - a.latestDonationDate);
  }, [donors, searchFilter, modeFilter, dateFilter]);

  const paginatedDonors = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return groupedDonors.slice(startIndex, startIndex + itemsPerPage);
  }, [groupedDonors, currentPage, itemsPerPage]);

  // Returns the next sequential Nidhi year (last paid year + 1)
  const getNextNidhiYear = (donor) => {
    const successfulNidhis = (donor?.donations || []).filter(
      d => d.donationType === EF_TYPE && d.txnStatus === "Success"
    );
    if (successfulNidhis.length === 0) return new Date().getFullYear();
    return Math.max(...successfulNidhis.map(d => new Date(d.date).getFullYear())) + 1;
  };

  const handleDonorSelect = (donor) => {
    setSelectedDonor(donor);
    if (!editDonationData) {
      // Keep today as payment date, but show the next Nidhi year for info
      setForm(prev => ({ ...prev, date: new Date().toISOString().split("T")[0] }));
    }
  };

  const handleClearDonor = () => {
    setSelectedDonor(null); setEditDonationData(null); setAgreed(false);
    setForm({ donationType: EF_TYPE, amount: "5000", date: new Date().toISOString().split("T")[0], mode: "Cash", refNo: "", receiptNo: "" });
  };

  const toggleMainRow = (donorId) => {
    setExpandedRow(expandedRow === donorId ? null : donorId);
    setExpandedDonationId(null); 
  };

  const handleProcessPayment = async () => {
    if (!selectedDonor) return Swal.fire("Warning", "Please select a donor first.", "warning");
    
    if (missingProfileFields.length > 0) {
      return Swal.fire(
        "Incomplete Profile for Nidhi",
        `Protsahan Nidhi requires a fully completed donor profile. The following fields are missing or invalid: \n\n${missingProfileFields.join(", ")}.\n\nPlease edit this donor profile in Donor Management first.`,
        "warning"
      );
    }

    if (!form.date) return Swal.fire("Warning", "Date is required.", "warning");
    
    if (form.mode !== "Cash" && (!form.refNo || form.refNo.trim() === "")) {
      return Swal.fire("Warning", "Reference / Cheque Number is mandatory for all non-Cash payment modes.", "warning");
    }

    if (!form.receiptNo || form.receiptNo.trim() === "") {
      return Swal.fire("Warning", "Receipt No (रसीद नं.) is required for manual Nidhi transactions.", "warning");
    }

    if (isFirstTimeDonor && !agreed) {
      return Swal.fire("Warning", "कृपया प्रोत्साहन निधि सदस्यता घोषणा पत्र से सहमत होने के लिए चेकबॉक्स पर टिक करें।", "warning");
    }

    try {
      if (editDonationData) {
        await axiosInstance.put(`/api/donors/${editDonationData.donorId}/donate/${editDonationData.donationId}`, form);
        Swal.fire("Updated", "Donation updated successfully", "success");
      } else {
        await axiosInstance.post(`/api/donors/${selectedDonor._id}/donate`, form);
        Swal.fire("Success", "Donation added successfully", "success");
      }
      handleClearDonor(); setShowAddForm(false); fetchDonors();
    } catch (err) { Swal.fire("Error", "Failed to process donation", "error"); }
  };

  const handleEditDonation = (donation) => {
    const donor = donors.find((d) => d._id === donation.donorId);
    setSelectedDonor(donor);
    setForm({
      donationType: EF_TYPE, amount: "5000",
      date: new Date(donation.date).toISOString().split("T")[0], mode: donation.mode, refNo: donation.refNo || "", receiptNo: donation.receiptNo || ""
    });
    setAgreed(true); 
    setEditDonationData({ donorId: donation.donorId, donationId: donation._id });
    setShowAddForm(true); window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteDonation = async (donorId, donationId) => {
    const res = await Swal.fire({ title: "Remove Donation?", text: "This action cannot be undone.", icon: "warning", showCancelButton: true, confirmButtonColor: "#e74c3c" });
    if (res.isConfirmed) {
      await axiosInstance.delete(`/api/donors/${donorId}/donate/${donationId}`);
      fetchDonors();
    }
  };

  const toggleDonorStatus = async (donorId, currentStatus) => {
    try {
      await axiosInstance.put(`/api/donors/${donorId}/status`, { isActive: !currentStatus });
      fetchDonors();
    } catch (err) {
      Swal.fire("Error", "Could not update donor status", "error");
    }
  };

  const toggleDonationStatus = async (donorId, donationId, currentStatus) => {
    try {
      await axiosInstance.put(`/api/donors/${donorId}/donate/${donationId}/status`, { isActive: !currentStatus });
      fetchDonors();
    } catch (err) {
      Swal.fire("Error", "Could not update status", "error");
    }
  };

  const handleExportExcel = async () => {
    if (groupedDonors.length === 0) return Swal.fire('Notice', 'No data to export.', 'info');

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Protsahan Nidhi Database');

    // Merged cell range shifted to L1 (12 columns)
    worksheet.mergeCells('A1:L1');
    worksheet.getCell('A1').value = 'RAJPUT SHIKSHA KOSH';
    worksheet.getCell('A1').font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FFB91C1C' } };
    worksheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };

    worksheet.mergeCells('A2:L2');
    worksheet.getCell('A2').value = 'Protsahan Nidhi (प्रोत्साहन निधि) Statement Report';
    worksheet.getCell('A2').font = { name: 'Arial', size: 12, bold: true, color: { argb: 'FF2C3E50' } };
    worksheet.getCell('A2').alignment = { vertical: 'middle', horizontal: 'center' };

    worksheet.mergeCells('A3:L3');
    worksheet.getCell('A3').value = `Search Filter: ${searchFilter || 'All'}  |  Date Filter: ${dateFilter || 'All'}  |  Mode Filter: ${modeFilter || 'All'}`;
    worksheet.getCell('A3').font = { name: 'Arial', size: 10, italic: true, color: { argb: 'FF64748B' } };
    worksheet.getCell('A3').alignment = { vertical: 'middle', horizontal: 'center' };

    worksheet.addRow([]);

    // Headers updated to place 'Date' right before 'Year'
    const headers = [
      'S.No', 'Member ID', 'Donor Name', 'Father Name', 'Contact Number', 
      'Current Address', 'Date', 'Year', 'Amount (₹)', 'Payment Mode', 'Reference Number', 'Status'
    ];

    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF34495E' } };
      cell.font = { color: { argb: 'FFFFFFFF' }, bold: true, name: 'Arial', size: 10 };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });

    let serialNo = 1;
    groupedDonors.forEach((donor) => {
      // Sort ascending for correct sequential year display in export
      const sortedAsc = [...donor.nidhiDonationsList].sort((a, b) => {
        const dateDiff = new Date(a.date) - new Date(b.date);
        if (dateDiff !== 0) return dateDiff;
        return a._id.localeCompare(b._id);
      });
      const anchorYear = sortedAsc.length > 0 ? new Date(sortedAsc[0].date).getFullYear() : new Date().getFullYear();

      sortedAsc.forEach((don, idx) => {
        const sequentialYear = anchorYear + idx;
        const dateOnly = new Date(don.date).toLocaleDateString('en-IN');
        
        const row = worksheet.addRow([
          serialNo++,
          donor.memberId || 'N/A',
          donor.name,
          donor.fatherName || '—',
          donor.mobileNo || '—',
          donor.currentAddress || '—',
          dateOnly,
          sequentialYear,
          don.amount,
          don.mode || 'CASH',
          don.refNo || '—',
          don.isActive ? 'Active' : 'Disabled'
        ]);

        row.eachCell((cell, colNumber) => {
          cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
          cell.font = { name: 'Arial', size: 9 };
          if ([3, 4, 6].includes(colNumber)) {
            cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
          } else if (colNumber === 9) { 
            cell.alignment = { vertical: 'middle', horizontal: 'right', wrapText: true };
            cell.numFmt = '₹#,##0.00';
          } else {
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
          }
        });

        const statusCell = row.getCell(12);
        statusCell.font = { bold: true, name: 'Arial', size: 9, color: { argb: don.isActive ? 'FF16A34A' : 'FF94A3B8' } };
      });
    });

    worksheet.columns = [
      { width: 8 },   // S.No
      { width: 14 },  // Member ID
      { width: 22 },  // Donor Name
      { width: 20 },  // Father Name
      { width: 15 },  // Contact Number
      { width: 35 },  // Current Address
      { width: 14 },  // Date (New Column width)
      { width: 10 },  // Year
      { width: 15 },  // Amount
      { width: 18 },  // Payment Mode
      { width: 22 },  // Reference Number
      { width: 12 }   // Status
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'Nidhi_Donations_Database.xlsx');
  };

  return (
    <>
      <Helmet>
        <title>Nidhi Donations | Rajput Shiksha Kosh</title>
        <meta name="description" content="Manage Nidhi Donations for the Rajput Shiksha Kosh organization." />
      </Helmet>
      
      <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-container">
        <h2 className="protsahan-nidhi-donation-list-in-admin-add-nidhi-title">
          <FaMoneyBillWave style={{ marginRight: "8px" }} /> प्रोत्साहन निधि Donations
        </h2>

        <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-controls">
          <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-search-wrapper">
            <FaSearch className="protsahan-nidhi-donation-list-in-admin-add-nidhi-search-icon" />
            <input 
              type="text" 
              className="protsahan-nidhi-donation-list-in-admin-add-nidhi-search-input" 
              placeholder="Search ID, Name, or Mobile..." 
              value={searchFilter} 
              onChange={(e) => { setSearchFilter(e.target.value); setCurrentPage(1); }} 
            />
          </div>
          
          <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-right-controls">
            <input 
              type="date" 
              value={dateFilter} 
              onChange={(e) => { setDateFilter(e.target.value); setCurrentPage(1); }} 
              className="protsahan-nidhi-donation-list-in-admin-add-nidhi-items-select" 
              title="Filter by Specific Date" 
            />
            <select value={modeFilter} onChange={(e) => { setModeFilter(e.target.value); setCurrentPage(1); }} className="protsahan-nidhi-donation-list-in-admin-add-nidhi-items-select">
              <option value="">All Modes</option>
              {MODES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="protsahan-nidhi-donation-list-in-admin-add-nidhi-items-select">
              <option value="5">5 / page</option>
              <option value="10">10 / page</option>
              <option value="20">20 / page</option>
              <option value="50">50 / page</option>
            </select>
            <button className="protsahan-nidhi-donation-list-in-admin-add-nidhi-btn protsahan-nidhi-donation-list-in-admin-add-nidhi-btn-export" onClick={handleExportExcel} title="Export to Excel">
              <FaFileExport /> Export
            </button>
            <button className="protsahan-nidhi-donation-list-in-admin-add-nidhi-btn protsahan-nidhi-donation-list-in-admin-add-nidhi-btn-add" onClick={() => { setShowAddForm(!showAddForm); handleClearDonor(); }}>
              {showAddForm ? <><FaTimes /> Close Form</> : <><FaPlus /> प्रोत्साहन निधि जोड़ें</>}
            </button>
          </div>
        </div>

        {showAddForm && (
          <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-inline-form">
            <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-form-header">
               <h3 className="protsahan-nidhi-donation-list-in-admin-add-nidhi-form-title">{editDonationData ? "✏️ Edit Nidhi Donation" : "📝 Record Nidhi Donation (Manual)"}</h3>
            </div>
            
            <h4 className="protsahan-nidhi-donation-list-in-admin-add-nidhi-section-title">Step 1: Select Donor</h4>
            <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-form-grid">
              <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-form-group"><label>By Member ID</label><DonorDropdown value={selectedDonor?.memberId || ""} placeholder="Search Member ID" donors={activeDonors} searchKey="memberId" onSelect={handleDonorSelect} onClear={handleClearDonor} disabled={editDonationData}/></div>
              <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-form-group"><label>By Name</label><DonorDropdown value={selectedDonor?.name || ""} placeholder="Search Name" donors={activeDonors} searchKey="name" onSelect={handleDonorSelect} onClear={handleClearDonor} disabled={editDonationData}/></div>
              <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-form-group"><label>By Mobile No</label><DonorDropdown value={selectedDonor?.mobileNo || ""} placeholder="Search Mobile" donors={activeDonors} searchKey="mobileNo" onSelect={handleDonorSelect} onClear={handleClearDonor} disabled={editDonationData}/></div>
            
              {selectedDonor && (
                <div style={{
                  gridColumn: "1 / -1",
                  padding: "12px",
                  borderRadius: "6px",
                  background: missingProfileFields.length > 0 ? "#fff5f5" : "#f0fdf4",
                  border: `1px solid ${missingProfileFields.length > 0 ? "#feb2b2" : "#bbf7d0"}`,
                  marginTop: "10px",
                  fontSize: "0.85rem"
                }}>
                  {missingProfileFields.length > 0 ? (
                    <span style={{ color: "#c53030", fontWeight: "600" }}>
                      ⚠️ This profile is incomplete for Protsahan Nidhi. Please update the donor profile in Donor Management with: {missingProfileFields.join(", ")}.
                    </span>
                  ) : (
                    <span style={{ color: "#2f855a", fontWeight: "600" }}>
                      ✅ Selected Donor profile contains all information required for Protsahan Nidhi.
                    </span>
                  )}
                </div>
              )}
            </div>

            <h4 className="protsahan-nidhi-donation-list-in-admin-add-nidhi-section-title">Step 2: Donation Details</h4>
            <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-form-grid">
              <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-form-group protsahan-nidhi-donation-list-in-admin-add-nidhi-col-span-2">
                <label>Donation Type</label>
                <input type="text" value="प्रोत्साहन निधि" readOnly className="protsahan-nidhi-donation-list-in-admin-add-nidhi-input-readonly" />
              </div>

              <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-form-group">
                <label>Amount (₹)</label>
                <input type="text" value={form.amount} readOnly className="protsahan-nidhi-donation-list-in-admin-add-nidhi-input-readonly" style={{ fontWeight: "700", color: "#e67e22" }} />
              </div>

              <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-form-group">
                <label>Payment Date <span className="protsahan-nidhi-donation-list-in-admin-add-nidhi-req">*</span></label>
                <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>

              {!editDonationData && selectedDonor && (
                <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-form-group">
                  <label>Nidhi Year (Auto)</label>
                  <input
                    type="text"
                    readOnly
                    value={getNextNidhiYear(selectedDonor)}
                    className="protsahan-nidhi-donation-list-in-admin-add-nidhi-input-readonly"
                    style={{ fontWeight: "700", color: "#2563eb" }}
                    title="This payment will be recorded under this year"
                  />
                </div>
              )}

              <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-form-group">
                <label>Payment Mode <span className="protsahan-nidhi-donation-list-in-admin-add-nidhi-req">*</span></label>
                <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
                  {MODES.map((m) => (<option key={m} value={m}>{m}</option>))}
                </select>
              </div>

              <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-form-group">
                <label>Receipt No (रसीद नं.) <span className="protsahan-nidhi-donation-list-in-admin-add-nidhi-req">*</span></label>
                <input type="text" value={form.receiptNo} onChange={(e) => setForm({ ...form, receiptNo: e.target.value })} placeholder="e.g. RSK-ADM-001" />
              </div>

              {form.mode !== "Cash" && (
                <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-form-group">
                  <label>Ref / Cheque No <span className="protsahan-nidhi-donation-list-in-admin-add-nidhi-req">*</span></label>
                  <input type="text" value={form.refNo} onChange={(e) => setForm({ ...form, refNo: e.target.value })} placeholder="Reference Details" />
                </div>
              )}
            </div>

            {isFirstTimeDonor && (
              <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-declaration-box">
                 <h4 className="protsahan-nidhi-donation-list-in-admin-add-nidhi-dec-heading">घोषणा पत्र</h4>
                 <p className="protsahan-nidhi-donation-list-in-admin-add-nidhi-dec-text">
                   मैं घोषणा करता/करती हूँ कि राजपूत शिक्षा कोष द्वारा प्रायोजित राजपूत शिक्षा प्रोत्साहन निधि की स्वेच्छा से आजीवन सदस्यता ग्रहण कर रहा/रही हूँ जिसके तहत शुल्क 5,000 रुपये प्रतिवर्ष आर्थिक शिक्षा सहयोग देता रहूँगा/देती रहूँगी।
                 </p>
                 <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-dec-agreement">
                   <p className="protsahan-nidhi-donation-list-in-admin-add-nidhi-agree-label">मैं उपरोक्त घोषणा से सहमत हूँ। <span className="protsahan-nidhi-donation-list-in-admin-add-nidhi-req">*</span></p>
                   <label className="protsahan-nidhi-donation-list-in-admin-add-nidhi-checkbox-holder">
                     <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
                     <span>हाँ, मैं सहमत हूँ।</span>
                   </label>
                 </div>
              </div>
            )}

            <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-form-buttons">
              <button className="protsahan-nidhi-donation-list-in-admin-add-nidhi-btn-cancel" onClick={() => setShowAddForm(false)}>Cancel</button>
              <button className="protsahan-nidhi-donation-list-in-admin-add-nidhi-btn-confirm" onClick={handleProcessPayment}>{editDonationData ? "Update Donation" : "Submit Donation"}</button>
            </div>
          </div>
        )}

        <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-table-wrapper">
          <table className="protsahan-nidhi-donation-list-in-admin-add-nidhi-table">
            <thead>
              <tr>
                <th className="protsahan-nidhi-donation-list-in-admin-add-nidhi-col-sno">S.No</th>
                <th className="protsahan-nidhi-donation-list-in-admin-add-nidhi-col-memberid">Member ID</th>
                <th className="protsahan-nidhi-donation-list-in-admin-add-nidhi-col-name protsahan-nidhi-donation-list-in-admin-add-nidhi-align-left">Donor Name</th>
                <th className="protsahan-nidhi-donation-list-in-admin-add-nidhi-col-fathername">Father Name</th>
                <th className="protsahan-nidhi-donation-list-in-admin-add-nidhi-col-contact">Contact Number</th>
                <th className="protsahan-nidhi-donation-list-in-admin-add-nidhi-col-status">Status</th>
                <th className="protsahan-nidhi-donation-list-in-admin-add-nidhi-col-action">Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedDonors.length === 0 ? (
                  <tr><td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No encouragement fund records found.</td></tr>
              ) : paginatedDonors.map((donor, index) => {
                const serialNo = (currentPage - 1) * itemsPerPage + index + 1;
                const reversedNidhis = [...donor.nidhiDonationsList].reverse();
                const firstPayment = reversedNidhis[0];

                return (
                  <React.Fragment key={donor._id}>
                    <tr className="protsahan-nidhi-donation-list-in-admin-add-nidhi-main-row">
                      <td className="protsahan-nidhi-donation-list-in-admin-add-nidhi-col-sno" style={{ fontWeight: "600", color: "#64748b" }}>{serialNo}</td>
                      
                      <td className="protsahan-nidhi-donation-list-in-admin-add-nidhi-col-memberid" style={{ color: donor.memberId ? "#334155" : "#e67e22", fontWeight: "700" }}>
                          {donor.memberId || 'N/A'}
                      </td>
                      
                      <td className="protsahan-nidhi-donation-list-in-admin-add-nidhi-col-name protsahan-nidhi-donation-list-in-admin-add-nidhi-align-left">
                        <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-name-wrapper" onClick={() => toggleMainRow(donor._id)}>
                          <div style={{display: 'flex', flexDirection: 'column'}}>
                             <span style={{ fontWeight: 600 }}>{donor.name}</span>
                             <span style={{ fontSize: '0.75rem', color: '#64748b' }} className="protsahan-nidhi-donation-list-in-admin-add-nidhi-mobile-only-contact-label">{donor.mobileNo}</span>
                          </div>
                          <button className="protsahan-nidhi-donation-list-in-admin-add-nidhi-expand-btn">
                            {expandedRow === donor._id ? <FaChevronUp /> : <FaChevronDown />}
                          </button>
                        </div>
                      </td>

                      <td className="protsahan-nidhi-donation-list-in-admin-add-nidhi-col-fathername" style={{ color: "#334155" }}>{donor.fatherName || "—"}</td>

                      <td className="protsahan-nidhi-donation-list-in-admin-add-nidhi-col-contact" style={{ fontWeight: "600", color: "#334155" }}>{donor.mobileNo}</td>
                      
                      <td className="protsahan-nidhi-donation-list-in-admin-add-nidhi-col-status">
                        <label className="protsahan-nidhi-donation-list-in-admin-add-nidhi-switch" title={donor.isActive ? "Active" : "Inactive"}>
                          <input type="checkbox" checked={donor.isActive} onChange={() => toggleDonorStatus(donor._id, donor.isActive)} />
                          <span className="protsahan-nidhi-donation-list-in-admin-add-nidhi-slider protsahan-nidhi-donation-list-in-admin-add-nidhi-round"></span>
                        </label>
                        <div style={{ fontSize: '0.75rem', marginTop: '4px', color: donor.isActive ? '#e67e22' : '#94a3b8', fontWeight: '600' }}>
                          {donor.isActive ? 'Active' : 'Disabled'}
                        </div>
                      </td>

                      <td className="protsahan-nidhi-donation-list-in-admin-add-nidhi-col-action">
                        <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-action-btns">
                          {firstPayment && (
                             <button className="protsahan-nidhi-donation-list-in-admin-add-nidhi-btn-form" onClick={() => navigate("/admin/lifetime-member", { state: { donation: firstPayment, isFirstTime: true } })} title="Print Membership Application Form"><FaPrint /> Form</button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {expandedRow === donor._id && (
                      <tr className="protsahan-nidhi-donation-list-in-admin-add-nidhi-expanded-row">
                          <td colSpan="7">
                              <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-expanded-content">
                                  
                                  {/* Multi-Breakpoints Donor Profile widgets (Renders fully separated widget blocks per screen width limits, no left border) */}
                                  <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-donor-profile-summary">
                                    {/* Member ID card: visible only < 768px */}
                                    <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-summary-item protsahan-nidhi-donation-list-in-admin-add-nidhi-show-lt-768">
                                      <span className="protsahan-nidhi-donation-list-in-admin-add-nidhi-summary-label">Member ID</span>
                                      <span className="protsahan-nidhi-donation-list-in-admin-add-nidhi-summary-val">{donor.memberId || 'N/A'}</span>
                                    </div>
                                    
                                    {/* Father Name card: visible < 1200px (i.e. <768px, 768-991px, 991-1200px) */}
                                    <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-summary-item protsahan-nidhi-donation-list-in-admin-add-nidhi-show-lt-768 protsahan-nidhi-donation-list-in-admin-add-nidhi-show-768-991 protsahan-nidhi-donation-list-in-admin-add-nidhi-show-991-1200">
                                      <span className="protsahan-nidhi-donation-list-in-admin-add-nidhi-summary-label">Father's Name</span>
                                      <span className="protsahan-nidhi-donation-list-in-admin-add-nidhi-summary-val">{donor.fatherName || '—'}</span>
                                    </div>
                                    
                                    {/* Contact card: visible only < 991px (i.e. <768px, 768-991px) */}
                                    <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-summary-item protsahan-nidhi-donation-list-in-admin-add-nidhi-show-lt-768 protsahan-nidhi-donation-list-in-admin-add-nidhi-show-768-991">
                                      <span className="protsahan-nidhi-donation-list-in-admin-add-nidhi-summary-label">Contact Number</span>
                                      <span className="protsahan-nidhi-donation-list-in-admin-add-nidhi-summary-val">{donor.mobileNo}</span>
                                    </div>
                                    
                                    {/* Status Switch card: visible < 991px (i.e. <768px, 768-991px) */}
                                    <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-summary-item protsahan-nidhi-donation-list-in-admin-add-nidhi-show-lt-768 protsahan-nidhi-donation-list-in-admin-add-nidhi-show-768-991">
                                      <span className="protsahan-nidhi-donation-list-in-admin-add-nidhi-summary-label">Status</span>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                                        <label className="protsahan-nidhi-donation-list-in-admin-add-nidhi-switch">
                                          <input type="checkbox" checked={donor.isActive} onChange={() => toggleDonorStatus(donor._id, donor.isActive)} />
                                          <span className="protsahan-nidhi-donation-list-in-admin-add-nidhi-slider protsahan-nidhi-donation-list-in-admin-add-nidhi-round"></span>
                                        </label>
                                        <span style={{ fontSize: '0.8rem', fontWeight: 'bold', color: donor.isActive ? '#e67e22' : '#94a3b8' }}>
                                          {donor.isActive ? 'Active' : 'Disabled'}
                                        </span>
                                      </div>
                                    </div>
                                    
                                    {/* Form Action card: visible only < 768px */}
                                    <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-summary-item protsahan-nidhi-donation-list-in-admin-add-nidhi-show-lt-768">
                                      <span className="protsahan-nidhi-donation-list-in-admin-add-nidhi-summary-label">Action</span>
                                      <div style={{ marginTop: '4px' }}>
                                        {firstPayment && (
                                          <button className="protsahan-nidhi-donation-list-in-admin-add-nidhi-btn-form" onClick={() => navigate("/admin/lifetime-member", { state: { donation: firstPayment, isFirstTime: true } })} title="Print Membership Application Form"><FaPrint /> Form</button>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Subtable list of this donor's Nidhi transactions */}
                                  <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-subtable-wrapper">
                                    <table className="protsahan-nidhi-donation-list-in-admin-add-nidhi-subtable">
                                      <thead>
                                        <tr>
                                          <th className="protsahan-nidhi-donation-list-in-admin-add-nidhi-subcol-sno">S.No</th>
                                          <th className="protsahan-nidhi-donation-list-in-admin-add-nidhi-subcol-date">Date & Time</th>
                                          <th className="protsahan-nidhi-donation-list-in-admin-add-nidhi-subcol-receipt">Receipt No</th>
                                          <th className="protsahan-nidhi-donation-list-in-admin-add-nidhi-subcol-year">Year</th>
                                          <th className="protsahan-nidhi-donation-list-in-admin-add-nidhi-subcol-amount">Amount</th>
                                          <th className="protsahan-nidhi-donation-list-in-admin-add-nidhi-subcol-mode">Payment Mode</th>
                                          <th className="protsahan-nidhi-donation-list-in-admin-add-nidhi-subcol-status">Status</th>
                                          <th className="subcol-action">Action</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(() => {
                                          const sortedAsc = [...donor.nidhiDonationsList].sort((a, b) => {
                                            const dateDiff = new Date(a.date) - new Date(b.date);
                                            if (dateDiff !== 0) return dateDiff;
                                            return a._id.localeCompare(b._id);
                                          });
                                          const anchorYear = sortedAsc.length > 0 ? new Date(sortedAsc[0].date).getFullYear() : new Date().getFullYear();
                                          const yearMap = {};
                                          sortedAsc.forEach((d, i) => { yearMap[d._id] = anchorYear + i; });
                                          return donor.nidhiDonationsList.map((don, idx) => {
                                          const isPGTransaction = Boolean(don.merchantTxnNo) || Boolean(don.pgPaymentId) || Boolean(don.receiptNo && (don.receiptNo.includes('ONL') || don.receiptNo.includes('PUB')));
                                          const year = yearMap[don._id] ?? new Date(don.date).getFullYear();
                                          const isSubExpanded = expandedDonationId === don._id;
                                          
                                          const isCash = don.mode === "Cash";
                                          const hasRef = don.refNo && don.refNo.trim() !== "";
                                          const showRefBlock = isPGTransaction || (!isCash && hasRef);

                                          return (
                                            <React.Fragment key={don._id}>
                                              <tr className="protsahan-nidhi-donation-list-in-admin-add-nidhi-subtable-row">
                                                <td className="protsahan-nidhi-donation-list-in-admin-add-nidhi-subcol-sno" data-label="S.No">{idx + 1}</td>
                                                <td className="protsahan-nidhi-donation-list-in-admin-add-nidhi-subcol-date" data-label="Date & Time">
                                                  <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-sub-date-block">
                                                    <div style={{ fontWeight: "600" }}>{new Date(don.date).toLocaleDateString("en-IN")}</div>
                                                    <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
                                                      {/^[a-fA-F0-9]{24}$/.test(don._id) ? new Date(parseInt(don._id.substring(0, 8), 16) * 1000).toLocaleTimeString("en-IN", { hour: '2-digit', minute: '2-digit' }) : ""}
                                                    </div>
                                                  </div>
                                                </td>
                                                <td className="protsahan-nidhi-donation-list-in-admin-add-nidhi-subcol-receipt" data-label="Receipt No">
                                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                    <span style={{ 
                                                      background: '#eff6ff', color: '#3b82f6', 
                                                      padding: '4px 8px', borderRadius: '4px', fontSize: '0.8rem', border: '1px solid #bfdbfe', fontWeight: '700'
                                                    }}>{don.receiptNo || '—'}</span>
                                                    
                                                    <button 
                                                      className="protsahan-nidhi-donation-list-in-admin-add-nidhi-sub-expand-btn"
                                                      onClick={() => setExpandedDonationId(isSubExpanded ? null : don._id)}
                                                      title="Toggle Transaction Details"
                                                    >
                                                      {isSubExpanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                                                    </button>
                                                  </div>
                                                </td>
                                                <td className="protsahan-nidhi-donation-list-in-admin-add-nidhi-subcol-year" data-label="Year" style={{ fontWeight: "600" }}>{year}</td>
                                                <td className="protsahan-nidhi-donation-list-in-admin-add-nidhi-subcol-amount" data-label="Amount" style={{ fontWeight: "700", color: "#e67e22" }}>₹{don.amount}</td>
                                                <td className="protsahan-nidhi-donation-list-in-admin-add-nidhi-subcol-mode" data-label="Payment Mode">
                                                  <div style={{ fontSize: "0.85rem", fontWeight: 600, color: '#334155' }}>
                                                    {isPGTransaction ? `🌐 Gateway` : `🏢 ${don.mode}`}
                                                  </div>
                                                </td>
                                                <td className="protsahan-nidhi-donation-list-in-admin-add-nidhi-subcol-status" data-label="Status">
                                                  <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-sub-toggle-block">
                                                    <label className="protsahan-nidhi-donation-list-in-admin-add-nidhi-switch">
                                                      <input type="checkbox" checked={don.isActive} onChange={() => toggleDonationStatus(don.donorId, don._id, don.isActive)} />
                                                      <span className="protsahan-nidhi-donation-list-in-admin-add-nidhi-slider protsahan-nidhi-donation-list-in-admin-add-nidhi-round"></span>
                                                    </label>
                                                    <div style={{ fontSize: '0.72rem', marginTop: '2px', color: don.isActive ? '#e67e22' : '#94a3b8', fontWeight: '600' }}>
                                                      {don.isActive ? 'Active' : 'Disabled'}
                                                    </div>
                                                  </div>
                                                </td>
                                                <td className="subcol-action" data-label="Action">
                                                  <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-donation-action-btns">
                                                    <button className="protsahan-nidhi-donation-list-in-admin-add-nidhi-btn-print" onClick={() => navigate("/admin/donation-receipt", { state: { donation: don } })} title="Print Receipt"><FaPrint /> Receipt</button>
                                                    
                                                    {isPGTransaction ? (
                                                      <span className="protsahan-nidhi-donation-list-in-admin-add-nidhi-locked" title="Online Transactions Cannot be Edited"><FaLock /></span>
                                                    ) : (
                                                      <>
                                                        <button className="protsahan-nidhi-donation-list-in-admin-add-nidhi-btn-edit" onClick={() => handleEditDonation(don)} title="Edit Details"><FaEdit /></button>
                                                        <button className="protsahan-nidhi-donation-list-in-admin-add-nidhi-btn-delete" onClick={() => deleteDonation(don.donorId, don._id)} title="Delete Entry"><FaTrashAlt /></button>
                                                      </>
                                                    )}
                                                  </div>
                                                </td>
                                              </tr>

                                              {/* Nested Row displaying variables dynamically matching current screen needs in stunning widgets */}
                                              {isSubExpanded && (
                                                <tr className="protsahan-nidhi-donation-list-in-admin-add-nidhi-sub-details-row">
                                                  <td colSpan="8">
                                                    <div className={`protsahan-nidhi-donation-list-in-admin-add-nidhi-sub-details-wrapper ${isPGTransaction ? 'gateway-theme' : 'manual-theme'}`}>
                                                      
                                                      {/* Subtable values dynamic card grid based on display breakpoints */}
                                                      <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-pushed-values-grid">
                                                        
                                                        {/* Date & Time card: visible < 991px */}
                                                        <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-pushed-item protsahan-nidhi-donation-list-in-admin-add-nidhi-show-lt-768 protsahan-nidhi-donation-list-in-admin-add-nidhi-show-768-991">
                                                          <strong>Date & Time</strong>
                                                          <span>
                                                            {new Date(don.date).toLocaleDateString("en-IN")}{' '}
                                                            {/^[a-fA-F0-9]{24}$/.test(don._id) && new Date(parseInt(don._id.substring(0, 8), 16) * 1000).toLocaleTimeString("en-IN", { hour: '2-digit', minute: '2-digit' })}
                                                          </span>
                                                        </div>

                                                        {/* Year card: visible < 768px */}
                                                        <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-pushed-item protsahan-nidhi-donation-list-in-admin-add-nidhi-show-lt-768">
                                                          <strong>Year</strong>
                                                          <span>{year}</span>
                                                        </div>

                                                        {/* Amount card: visible < 768px */}
                                                        <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-pushed-item protsahan-nidhi-donation-list-in-admin-add-nidhi-show-lt-768">
                                                          <strong>Amount</strong>
                                                          <span style={{ fontWeight: '700', color: '#e67e22' }}>₹{don.amount}</span>
                                                        </div>

                                                        {/* Payment Mode card: visible < 1200px */}
                                                        <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-pushed-item protsahan-nidhi-donation-list-in-admin-add-nidhi-show-lt-768 protsahan-nidhi-donation-list-in-admin-add-nidhi-show-768-991 protsahan-nidhi-donation-list-in-admin-add-nidhi-show-991-1200">
                                                          <strong>Payment Mode</strong>
                                                          <span>{isPGTransaction ? 'Online Gateway' : don.mode}</span>
                                                        </div>

                                                        {/* Donation Status card: visible < 1200px */}
                                                        <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-pushed-item protsahan-nidhi-donation-list-in-admin-add-nidhi-show-lt-768 protsahan-nidhi-donation-list-in-admin-add-nidhi-show-768-991 protsahan-nidhi-donation-list-in-admin-add-nidhi-show-991-1200">
                                                          <strong>Donation Status</strong>
                                                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                                                            <label className="protsahan-nidhi-donation-list-in-admin-add-nidhi-switch">
                                                              <input type="checkbox" checked={don.isActive} onChange={() => toggleDonationStatus(don.donorId, don._id, don.isActive)} />
                                                              <span className="protsahan-nidhi-donation-list-in-admin-add-nidhi-slider protsahan-nidhi-donation-list-in-admin-add-nidhi-round"></span>
                                                            </label>
                                                            <span style={{ fontSize: '0.72rem', fontWeight: '600', color: don.isActive ? '#e67e22' : '#94a3b8' }}>
                                                              {don.isActive ? 'Active' : 'Disabled'}
                                                            </span>
                                                          </div>
                                                        </div>

                                                        {/* Actions card: visible < 1200px */}
                                                        <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-pushed-item protsahan-nidhi-donation-list-in-admin-add-nidhi-show-lt-768 protsahan-nidhi-donation-list-in-admin-add-nidhi-show-768-991 protsahan-nidhi-donation-list-in-admin-add-nidhi-show-991-1200">
                                                          <strong>Actions</strong>
                                                          <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-donation-action-btns" style={{ justifyContent: 'flex-start', marginTop: '4px' }}>
                                                            <button className="protsahan-nidhi-donation-list-in-admin-add-nidhi-btn-print" onClick={() => navigate("/admin/donation-receipt", { state: { donation: don } })} title="Print Receipt"><FaPrint /> Receipt</button>
                                                            {isPGTransaction ? (
                                                              <span className="protsahan-nidhi-donation-list-in-admin-add-nidhi-locked" title="Online Transactions Cannot be Edited"><FaLock /></span>
                                                            ) : (
                                                              <>
                                                                <button className="protsahan-nidhi-donation-list-in-admin-add-nidhi-btn-edit" onClick={() => handleEditDonation(don)} title="Edit Details"><FaEdit /></button>
                                                                <button className="protsahan-nidhi-donation-list-in-admin-add-nidhi-btn-delete" onClick={() => deleteDonation(don.donorId, don._id)} title="Delete Entry"><FaTrashAlt /></button>
                                                              </>
                                                            )}
                                                          </div>
                                                        </div>
                                                      </div>

                                                      {/* Transaction gateway or manual reference details. Separated into stunning dashboard widgets */}
                                                      {showRefBlock && (
                                                        <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-gateway-ref-box">
                                                          {isPGTransaction ? (
                                                            <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-sub-details-grid">
                                                              <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-metadata-card">
                                                                <strong>Bank Txn ID</strong>
                                                                <span>{don.refNo || '—'}</span>
                                                              </div>
                                                              <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-metadata-card">
                                                                <strong>PG Method</strong>
                                                                <span>{don.pgPaymentMode || 'UPI/Card'}</span>
                                                              </div>
                                                              <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-metadata-card">
                                                                <strong>UPI/Card ID</strong>
                                                                <span>{don.pgPaymentId || '—'}</span>
                                                              </div>
                                                              <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-metadata-card">
                                                                <strong>Order Ref</strong>
                                                                <span>{don.merchantTxnNo || '—'}</span>
                                                              </div>
                                                            </div>
                                                          ) : (
                                                            <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-sub-details-grid manual-details">
                                                              <div className="protsahan-nidhi-donation-list-in-admin-add-nidhi-metadata-card">
                                                                <strong>Ref / Cheque No</strong>
                                                                <span>{don.refNo || '—'}</span>
                                                              </div>
                                                            </div>
                                                          )}
                                                        </div>
                                                      )}

                                                    </div>
                                                  </td>
                                                </tr>
                                              )}
                                            </React.Fragment>
                                          );
                                          });
                                        })()}
                                      </tbody>
                                    </table>
                                  </div>

                              </div>
                          </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <Pagination totalItems={groupedDonors.length} itemsPerPage={itemsPerPage} currentPage={currentPage} onPageChange={setCurrentPage} />
      </div>
    </>
  );
};

export default AddNidhiDonation;